import React, { useState } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { ArrowBack } from '@mui/icons-material';
import DesignSystem from '../designSystem';
import { useThemeMode } from '../contexts/ThemeModeContext';
import { useAuth } from '../contexts/AuthContext';
import { getApiLanguage } from '../config/apiLanguage';
import { forgotPassword } from '../services/api';
import LocalePreferenceToggles from '../components/layout/LocalePreferenceToggles';
import { useT } from '../i18n/useT';

export default function ForgotPasswordPage() {
  useThemeMode();
  const t = useT();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const signInTarget = isAuthenticated ? '/homepage' : '/?login=1';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError(t('forgotEnterEmail'));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(t('forgotInvalidEmail'));
      return;
    }

    setIsLoading(true);
    try {
      const response = await forgotPassword(email, getApiLanguage());

      if (response.message && response.message.toLowerCase().includes('error')) {
        throw new Error(response.message);
      }

      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('forgotSendFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: DesignSystem.colors.canvas,
        padding: DesignSystem.spacing.lg,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          sx={{
            padding: DesignSystem.spacing.xxxl,
            borderRadius: DesignSystem.borderRadius.lg,
            border: `1px solid ${DesignSystem.colors.border}`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mb: DesignSystem.spacing.lg,
            }}
          >
            <Box sx={{ width: { xs: '100%', sm: 240 } }}>
              <LocalePreferenceToggles dense showLabels />
            </Box>
          </Box>

          {/* Back Button */}
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate(signInTarget)}
            sx={{
              mb: DesignSystem.spacing.lg,
              color: DesignSystem.colors.textSecondary,
              textTransform: 'none',
            }}
          >
            {t('forgotBackToLogin')}
          </Button>

          <Box sx={{ textAlign: 'center', mb: DesignSystem.spacing.xl }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: DesignSystem.typography.h1.fontWeight,
                color: DesignSystem.colors.textPrimary,
                mb: DesignSystem.spacing.sm,
              }}
            >
              {t('forgotTitle')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: DesignSystem.colors.textSecondary,
                fontSize: DesignSystem.typography.body.fontSize,
              }}
            >
              {t('forgotSubtitle')}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: DesignSystem.spacing.lg, borderRadius: DesignSystem.borderRadius.md }}>
              {error}
            </Alert>
          )}

          {success ? (
            <Box sx={{ textAlign: 'center' }}>
              <Alert severity="success" sx={{ mb: DesignSystem.spacing.lg, borderRadius: DesignSystem.borderRadius.md }}>
                {t('forgotSuccessTitle')}
              </Alert>
              <Typography variant="body1" sx={{ color: DesignSystem.colors.textSecondary, mb: DesignSystem.spacing.lg }}>
                {t('forgotSuccessBody', { email })}
              </Typography>
              <Typography variant="body2" sx={{ color: DesignSystem.colors.textSecondary, mb: DesignSystem.spacing.xl }}>
                {t('forgotSpamHint')}
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSuccess(false);
                  setEmail('');
                }}
                sx={{
                  mb: DesignSystem.spacing.md,
                  borderRadius: DesignSystem.borderRadius.xl,
                }}
              >
                {t('forgotTryDifferentEmail')}
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={() => navigate(signInTarget)}
                sx={{
                  borderRadius: DesignSystem.borderRadius.xl,
                }}
              >
                {t('forgotBackToLogin')}
              </Button>
            </Box>
          ) : (
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label={t('email')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                required
                disabled={isLoading}
                placeholder={t('forgotEmailPlaceholder')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: DesignSystem.borderRadius.md,
                  },
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{
                  mt: DesignSystem.spacing.xl,
                  mb: DesignSystem.spacing.md,
                  padding: `${DesignSystem.padding.button.paddingTop} ${DesignSystem.padding.button.paddingRight}`,
                  borderRadius: DesignSystem.borderRadius.xl,
                  fontWeight: DesignSystem.typography.button.fontWeight,
                  fontSize: DesignSystem.typography.button.fontSize,
                }}
              >
                {isLoading ? (
                  <CircularProgress size={24} sx={{ color: DesignSystem.colors.onPrimary }} />
                ) : (
                  t('forgotSendResetLink')
                )}
              </Button>

              <Box sx={{ textAlign: 'center', mt: DesignSystem.spacing.lg }}>
                <Typography variant="body2" sx={{ color: DesignSystem.colors.textSecondary }}>
                  {t('forgotRememberPassword')}{' '}
                  <Link
                    component={RouterLink}
                    to={signInTarget}
                    sx={{
                      color: DesignSystem.colors.primary,
                      textDecoration: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {t('loginSignIn')}
                  </Link>
                </Typography>
              </Box>
            </form>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
